<script lang="ts">
  import AppLayout from '@/layouts/AppLayout.svelte';
  import { apiClient } from '@/utils/api';

  interface HiddenConversation {
    id: string;
    other_participant: { id: string; name: string; avatar_url?: string };
    last_message_preview?: string;
    last_message_at?: string;
  }

  let items = $state<HiddenConversation[]>([]);
  let isLoading = $state(true);
  let workingId = $state<string | null>(null);

  $effect(() => {
    load();
  });

  async function load() {
    isLoading = true;
    const res = await apiClient.get<{ conversations: HiddenConversation[] }>('/api/v1/conversations?inbox=hidden');
    if (res.success && res.data) {
      // apiClient auto-converts snake_case → camelCase, so unwrap accordingly.
      const list = (res.data as any).conversations as HiddenConversation[] | undefined;
      items = (list ?? []).map((c: any) => ({
        id: c.id,
        other_participant: c.otherParticipant ?? c.other_participant,
        last_message_preview: c.lastMessagePreview ?? c.last_message_preview,
        last_message_at: c.lastMessageAt ?? c.last_message_at,
      }));
    }
    isLoading = false;
  }

  async function unhide(id: string) {
    workingId = id;
    try {
      const res = await apiClient.delete(`/api/v1/conversations/${id}/hide`);
      if (res.success) {
        items = items.filter(i => i.id !== id);
      }
    } finally {
      workingId = null;
    }
  }

  function formatDate(s?: string) {
    if (!s) return '';
    return new Date(s).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }
</script>

<svelte:head><title>숨김 대화 | AX Admin</title></svelte:head>

<AppLayout>
  <div class="max-w-3xl mx-auto p-6 space-y-6">
    <div>
      <h1 class="text-h1 text-content">숨김 대화</h1>
      <p class="mt-2 text-body2 text-content-tertiary">
        인박스에서 숨긴 대화 목록입니다. 차단과 달리 상대방은 여전히 메시지를 보낼 수 있어요.
      </p>
    </div>

    <div class="card">
      {#if isLoading}
        <div class="p-6 text-center text-content-tertiary">불러오는 중...</div>
      {:else if items.length === 0}
        <div class="p-10 text-center text-content-tertiary">숨겨놓은 대화가 없습니다.</div>
      {:else}
        <ul class="divide-y divide-default">
          {#each items as conv (conv.id)}
            <li class="flex items-center gap-3 p-4">
              <div class="w-10 h-10 rounded-full bg-primary-50 dark:bg-dark-tertiary text-primary dark:text-dark-text-high flex items-center justify-center font-semibold shrink-0">
                {conv.other_participant.name.charAt(0).toUpperCase()}
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-content truncate">{conv.other_participant.name}</p>
                <p class="text-body2 text-content-tertiary truncate">
                  {conv.last_message_preview || '메시지 없음'} · {formatDate(conv.last_message_at)}
                </p>
              </div>
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                disabled={workingId === conv.id}
                onclick={() => unhide(conv.id)}
              >
                {workingId === conv.id ? '처리 중...' : '숨김 해제'}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <div>
      <a href="/settings" class="text-body2 text-primary hover:underline">← 설정으로 돌아가기</a>
    </div>
  </div>
</AppLayout>
